import { AppConfigContextEnum, type AppConfig as GQLItem } from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';

/**
 * An application config — the API-consumer credential registry — as the superadmin screens read and
 * write it on the internal (VP staff) schema.
 *
 * Replaces the legacy `src/appConfig/` module (legacy interface: `apiClient`, `generated/graphql`,
 * nested under `app { … }`), which ADR-009 moved straight to InternalSchema without passing through
 * AppSchema.
 */

/**
 * Keyed off the generated enum rather than repeating its wire strings, like
 * `Internal/Order/OrderState` — a backend rename becomes a compile error here instead of a silently
 * dead comparison at a call site.
 */
export const AppContext = Object.freeze({
  APP: AppConfigContextEnum.App,
  BOX_OFFICE: AppConfigContextEnum.BoxOffice,
  CHECKOUT: AppConfigContextEnum.Checkout,
  CUSTOMER: AppConfigContextEnum.Customer,
  PUBLIC_API: AppConfigContextEnum.PublicApi,
});

export type AppContext = (typeof AppContext)[keyof typeof AppContext];

export type AppConfigType = {
  id: number;
  name?: string;
  applicationId?: string;
  appContext?: AppContext;
  urlBase?: string;
  origin?: string;
  enforceOrigin?: boolean;
};

/**
 * Mirrors the internal `AppConfigAttributes` input. Note there is no `applicationId`: the backend
 * generates it and the input type does not accept it, so carrying it here would claim a write that
 * never happens.
 */
export type AppConfigInput = {
  id?: number;
  name?: string;
  appContext?: AppContext;
  urlBase?: string;
  origin?: string;
  enforceOrigin?: boolean;
  loginUrl?: string;
};

export const fromGql = (item: GQLItem): AppConfigType => ({
  ...take<AppConfigType>(item, 'id'),
  // `id` aside, every field is nullable on the internal schema, and `take` passes `null` straight
  // through — it only skips `undefined`. Map them one by one so no `null` leaks into a
  // `T | undefined` field.
  name: item.name ?? undefined,
  applicationId: item.applicationId ?? undefined,
  appContext: item.appContext ?? undefined,
  urlBase: item.urlBase ?? undefined,
  origin: item.origin ?? undefined,
  enforceOrigin: item.enforceOrigin ?? undefined,
});

export const fromGqlCollection = (items: GQLItem[]): AppConfigType[] => items.map(fromGql);
